import * as React from 'react';
import * as classnames from 'classnames';
import * as _ from 'lodash';

import {
    Audience,
    HealthProblemType,
    PanelType,
    Project
} from '@nexxt/common/types';
import CountryDropdown from './CountryDropdown';
import HelperTooltip from '../../../common/HelperTooltip';
import ValidationService from '@nexxt/common/services/ValidationService';
import { dataTestid } from '../../../../constants/dataTestid';
interface AudienceSettingProps {
    isSaving: boolean;
    isEdited: boolean;
    audience: Audience;
    showError: boolean;
    currentProject: Project;
    validationService: ValidationService;
    projectHealth: any;
    handleProjectSettings: (keyName: string, value: any) => void;
    confirmPanelSwitching: (panelType: {
        value: PanelType;
        label: string;
    }) => void;
    onSaveAudience: () => void;
    onCountryChange: (countries: { value: string; label: string }[]) => void;
    disableByFieldName: (keyName?: string) => boolean;
}

const AudienceSetting = (props: AudienceSettingProps) => {
    const {
        handleProjectSettings,
        confirmPanelSwitching,
        disableByFieldName,
        audience,
        validationService,
        projectHealth
    } = props;
    const useIncaPanel = audience?.panelType === PanelType.INCA;
    const panels = [
        { value: PanelType.SELF_PROVIDED, label: 'Self-provided' },
        { value: PanelType.INCA, label: 'inca Panel' }
    ];

    const getWarningMessage = () => {
        const missingFields = [];
        if (audience.panelType === PanelType.INCA) {
            if (_.hasIn(projectHealth, 'lengthOfInterview'))
                missingFields.push('Length of Interview');
            if (
                _.hasIn(projectHealth, 'totalSampleSize') &&
                projectHealth['totalSampleSize'].type ==
                    HealthProblemType.WARNING
            )
                missingFields.push('Sample Size');
            if (_.hasIn(projectHealth, 'countries'))
                missingFields.push('Country');
        }
        return missingFields;
    };
    const getErrorMessage = () => {
        if (
            _.hasIn(projectHealth, 'totalSampleSize') &&
            projectHealth['totalSampleSize'].type == HealthProblemType.ERROR
        ) {
            return projectHealth['totalSampleSize']?.list?.[0]?.message;
        }
    };
    const checkError = (input) => validationService.isEmpty(input) || !input;
    const invalidFields = getWarningMessage();
    const sampleError = getErrorMessage();

    return (
        <div className="audience--panel-sourcing--container">
            <div className="audience--panel-sourcing">
                <div className="audience--panel-sourcing--item panel">
                    <span className="panel-label">Audience Sourcing</span>
                    <div
                        className={`panel-toggle ${
                            disableByFieldName() ? 'disabled' : ''
                        }`}
                    >
                        {panels.map((item, i) => (
                            <span
                                className={classnames('menu--item', {
                                    active: item.value == audience?.panelType
                                })}
                                data-test={dataTestid.MENU_INCA_PANEL}
                                key={`${i}-${item.value}`}
                                onClick={() => {
                                    if (item.value !== audience?.panelType) {
                                        confirmPanelSwitching(item);
                                    }
                                }}
                            >
                                {item.label}
                            </span>
                        ))}
                    </div>
                </div>
                <div className="audience--panel-sourcing--item">
                    <span
                        className={`panel-label ${
                            props.currentProject.countries?.length === 0 &&
                            useIncaPanel
                                ? 'error-text'
                                : ''
                        }`}
                    >
                        Country
                    </span>
                    <div>
                        <CountryDropdown
                            editEnabled={!disableByFieldName()}
                            useIncaPanel={useIncaPanel}
                            currentProject={props.currentProject}
                            onCountryChange={props.onCountryChange}
                        />
                    </div>
                </div>
                <div className="audience--panel-sourcing--item">
                    <span
                        className={`panel-label ${
                            checkError(audience.totalSampleSize) &&
                            props.showError
                                ? 'error-text'
                                : ''
                        }`}
                    >
                        Sample Size
                    </span>
                    <input
                        type="number"
                        name="sampleSize"
                        placeholder=""
                        className={`form-group__control ${
                            checkError(audience.totalSampleSize) &&
                            props.showError
                                ? 'error-border'
                                : ''
                        }`}
                        onChange={(e) =>
                            handleProjectSettings(
                                'totalSampleSize',
                                parseInt(e.target.value)
                            )
                        }
                        defaultValue={audience.totalSampleSize}
                        disabled={disableByFieldName('sample-size')}
                    />
                </div>
                {useIncaPanel ? (
                    <div className="audience--panel-sourcing--item">
                        <span
                            className={`panel-label ${
                                checkError(audience.lengthOfInterview) &&
                                useIncaPanel &&
                                props.showError
                                    ? 'error-text'
                                    : ''
                            }`}
                        >
                            Length of Interview
                        </span>
                        <input
                            type="number"
                            name="lengthOfInterview"
                            placeholder=""
                            className={`form-group__control ${
                                checkError(audience.lengthOfInterview) &&
                                useIncaPanel &&
                                props.showError
                                    ? 'error-border'
                                    : ''
                            }`}
                            pattern="[0-9]*"
                            onChange={(e) => {
                                handleProjectSettings(
                                    'lengthOfInterview',

                                    parseInt(e.target.value)
                                );
                            }}
                            defaultValue={audience.lengthOfInterview}
                            disabled={disableByFieldName()}
                        />
                    </div>
                ) : null}
            </div>
            {invalidFields?.length ? (
                <div className="audience--panel-sourcing--warning">
                    <p>{`To get a quote for using the inca panel, please provide "${invalidFields.join(
                        '", "'
                    )}"`}</p>
                </div>
            ) : sampleError ? (
                <div className="audience--panel-sourcing--warning">
                    <p>{sampleError}</p>
                </div>
            ) : null}
        </div>
    );
};

export default AudienceSetting;
